export const IMPLEMENTATION_DETAILS_PROMPT = 'Show implementation details for the changes you just made, including the exact prompt or configuration changes and how you verified them.';

const MUTATING_AGENT_TOOLS = new Set([
    'checkChatSourceConnection',
    'cloneCampaign',
    'connectChatSource',
    'createCampaign',
    'createSequence',
    'createSequenceStep',
    'createSignal',
    'createSnippet',
    'deleteSequence',
    'deleteSequenceStep',
    'deleteSnippet',
    'excludeContactFromCampaign',
    'updateCampaignCompanyExclusions',
    'excludeDomainsOrgWide',
    'importCompanies',
    'importContacts',
    'launchCampaign',
    'markInitialSetupComplete',
    'pauseCampaign',
    'rawUpsertContacts',
    'regenerateDraftReply',
    'replaceSequenceStepVariants',
    'runSignalOnAccounts',
    'setMailboxSignature',
    'setSequenceOpenTracking',
    'storeCampaignForwardingEmails',
    'storeDefaultReplyRecipients',
    'storeForwardResponseTypes',
    'storeForwardingEmails',
    'storeGlobalInstruction',
    'storeReplyInstructions',
    'storeSalesContext',
    'toggleAutopilot',
    'updateCampaignDailyLimit',
    'updateCampaignMeta',
    'updateCampaignSetup',
    'updateCampaignTables',
    'updateColdreachDB',
    'updateLinkedInConversationSuppression',
    'updateMailboxDefaultReplyRecipients',
    'updateSequenceDefaultReplyRecipients',
    'updateSequenceLinkedInAccounts',
    'updateSequenceMailboxes',
    'updateSequenceSchedule',
    'updateSequenceStep',
    'updateSignal',
    'updateSignalRefreshFrequency',
    'updateSnippet',
    'uploadEmailImage',
]);

interface ChatMessagePartLike {
    type: string;
    state?: string;
    output?: unknown;
}

interface ChatMessageLike {
    id?: string;
    role: string;
    parts?: readonly ChatMessagePartLike[];
}

export function shouldHandleCampaignLaunchOutput(
    part: ChatMessagePartLike,
    responseActive: boolean,
    hasHandledLaunch: boolean,
    campaignId?: number,
): boolean {
    if (!responseActive || hasHandledLaunch || !campaignId) return false;
    if (part.type !== 'tool-launchCampaign' || part.state !== 'output-available') return false;
    if (!part.output || typeof part.output !== 'object') return false;

    return (part.output as Record<string, unknown>).launched === true;
}

export function shouldShowImplementationDetailsAction(
    messages: readonly ChatMessageLike[],
    message: ChatMessageLike,
    isUnavailable: boolean,
    currentResponseMessageId?: string,
): boolean {
    if (isUnavailable || !currentResponseMessageId || message.role !== 'assistant' || messages.at(-1) !== message) return false;
    if (message.id !== currentResponseMessageId) return false;

    return message.parts?.some(part => {
        if (part.state !== 'output-available' || !part.type.startsWith('tool-')) return false;
        if (!MUTATING_AGENT_TOOLS.has(part.type.slice('tool-'.length))) return false;
        return toolOutputIndicatesChange(part.output);
    }) ?? false;
}

function toolOutputIndicatesChange(output: unknown): boolean {
    if (!output || typeof output !== 'object') return true;

    const result = output as Record<string, unknown>;
    if (typeof result.error === 'string' && result.error.trim()) return false;
    if (result.status === 'failed' || result.status === 'pending') return false;
    if (result.connected === false || result.alreadyConnected === true || result.toggled === false) return false;

    const outcomeFlags = [
        'connected',
        'created',
        'deleted',
        'excluded',
        'imported',
        'launched',
        'paused',
        'regenerated',
        'replaced',
        'started',
        'stored',
        'success',
        'updated',
    ];
    const presentFlags = outcomeFlags.filter(flag => typeof result[flag] === 'boolean');
    if (presentFlags.length > 0) return presentFlags.some(flag => result[flag] === true);

    return true;
}
